import React, { useRef } from 'react';
import { gsap } from 'gsap';
import { Observer } from 'gsap/Observer';
import { useGSAP } from '@gsap/react';
import ImageCard from './ImageCard';
import { useTheme } from '../hooks/useTheme';
import useMediaQuery from 'hooks/useMediaQuery';

gsap.registerPlugin(Observer);

const CardsCarousel = ({ cards = [], className = '' }) => {
  const { isDarkMode } = useTheme();
  const carouselRef = useRef(null);
  const wrapperRef = useRef(null);
  const isMobileSize = useMediaQuery();
  const position = useRef({ x: 0 });
  const itemWidthWithGap = useRef(0);

  const extendedCards = cards.length > 0 ? Array(5).fill(cards).flat() : [];

  useGSAP(
    () => {
      const carousel = carouselRef.current;
      const wrapper = wrapperRef.current;

      if (!carousel || !wrapper || cards.length === 0) return;

      const allCards = gsap.utils.toArray('.cards-carousel-item', wrapper);
      if (allCards.length === 0) return;

      let activeAnimation;
      let currentIndex = cards.length * 2;
      let lastActiveIndex = -1;
      let pressProxy = 0;
      let wheelTimeout;

      const mm = gsap.matchMedia();

      mm.add(
        {
          isDesktop: `(min-width: 769px)`,
          isMobile: `(max-width: 768px)`,
        },
        (context) => {
          const { isDesktop, isMobile } = context.conditions;

          const baseWidth = isDesktop ? '29.80dvw' : '82dvw';
          const activeWidth = isDesktop ? '45.39dvw' : '82.78dvw';
          const gap = isDesktop ? 32 : 10;
          const LIFT_ACTIVE_PX = isDesktop ? -42 : -21;
          const LIFT_ADJACENT_PX = isDesktop ? -12 : 0;

          const getDvwInPx = (dvwString) => {
            const value = parseFloat(dvwString);
            return window.innerWidth * (value / 100);
          };

          const calculateSizes = () => {
            const baseWidthInPx = getDvwInPx(baseWidth);
            itemWidthWithGap.current = baseWidthInPx + gap;

            const maxActiveWidthInPx = isDesktop
              ? baseWidthInPx * 2 + gap - baseWidthInPx
              : getDvwInPx(activeWidth);
            const activeWidthInPx = Math.min(
              getDvwInPx(activeWidth),
              maxActiveWidthInPx
            );

            gsap.set(wrapper, { gap: `${gap}px` });
            gsap.set(allCards, { width: baseWidthInPx });

            allCards.forEach((item) => {
              const cardIndex = parseInt(item.dataset.cardIndex, 10);
              const content = item.querySelector('.card-content');
              const isThisTheActiveCard =
                cardIndex === currentIndex % cards.length;

              gsap.set(content, {
                width: isThisTheActiveCard ? activeWidthInPx : baseWidthInPx,
                x: isThisTheActiveCard
                  ? (baseWidthInPx - activeWidthInPx) / 2
                  : 0,
              });
            });
          };

          const updateCardStates = (newIndex) => {
            if (newIndex === lastActiveIndex) return;
            lastActiveIndex = newIndex;

            const baseWidthInPx = getDvwInPx(baseWidth);
            const maxActiveWidthInPx = isDesktop
              ? baseWidthInPx * 2 + gap - baseWidthInPx
              : getDvwInPx(activeWidth);
            const activeWidthInPx = Math.min(
              getDvwInPx(activeWidth),
              maxActiveWidthInPx
            );

            allCards.forEach((item) => {
              const cardIndex = parseInt(item.dataset.cardIndex, 10);
              const isThisTheActiveCard = cardIndex === newIndex;
              const isAdjacent =
                cardIndex === (newIndex - 1 + cards.length) % cards.length ||
                cardIndex === (newIndex + 1) % cards.length;

              gsap.to(item.querySelector('.card-content'), {
                width: isThisTheActiveCard ? activeWidthInPx : baseWidthInPx,
                x: isThisTheActiveCard
                  ? (baseWidthInPx - activeWidthInPx) / 2
                  : 0,
                y: isThisTheActiveCard
                  ? LIFT_ACTIVE_PX
                  : isAdjacent
                  ? LIFT_ADJACENT_PX
                  : 0,
                duration: 0.4,
                ease: 'power2.out',
                overwrite: 'auto',
              });
            });
          };

          const updatePosition = () => {
            gsap.set(wrapper, { x: -position.current.x });

            if (isDesktop) {
              const viewportLeft = position.current.x;
              const viewportRight = viewportLeft + carousel.offsetWidth;
              let firstVisibleIndex = -1;

              // Add a small buffer to handle sub-pixel rendering differences
              // (e.g., 1000px vs 1000.005px)
              const buffer = 2;

              for (const card of allCards) {
                const cardLeft = card.offsetLeft;
                const cardRight = cardLeft + card.offsetWidth;

                // Update condition to include the buffer
                if (
                  cardLeft >= viewportLeft - buffer &&
                  cardRight <= viewportRight + buffer
                ) {
                  firstVisibleIndex = parseInt(card.dataset.cardIndex, 10);
                  break;
                }
              }

              // Only update if we found a valid index
              if (firstVisibleIndex !== -1) {
                updateCardStates(firstVisibleIndex);
              }
            } else {
              const newActiveIndex =
                Math.round(position.current.x / itemWidthWithGap.current) %
                cards.length;
              updateCardStates(newActiveIndex);
            }
          };

          const animateToCard = (index) => {
            currentIndex = index;
            activeAnimation?.kill();

            const targetX = index * itemWidthWithGap.current;
            const distance = Math.abs(position.current.x - targetX);
            const dynamicDuration = gsap.utils.clamp(
              0.25,
              0.7,
              distance / 2000
            );

            activeAnimation = gsap.to(position.current, {
              x: targetX,
              duration: dynamicDuration,
              ease: 'power3.out',
              onUpdate: updatePosition,
              onComplete: () => {
                const numCards = cards.length;
                const equivalentIndex =
                  (currentIndex % numCards) + numCards * 2;

                if (currentIndex !== equivalentIndex) {
                  currentIndex = equivalentIndex;
                  position.current.x = currentIndex * itemWidthWithGap.current;
                  updatePosition();
                }
              },
            });
          };

          calculateSizes();
          position.current.x = currentIndex * itemWidthWithGap.current;
          updatePosition();

          const observer = Observer.create({
            target: carousel,
            type: 'wheel,pointer,touch',
            dragMinimum: 5,
            // preventDefault: true, // <-- REMOVED THIS LINE
            onPress: (self) => {
              // We only prevent default on touch/pointer events to stop text selection, etc.
              if (self.type === 'pointer' || self.type === 'touch') {
                self.event.preventDefault();
              }
              activeAnimation?.kill();
              pressProxy = position.current.x;
              carousel.style.cursor = 'grabbing';
            },
            onDrag: (self) => {
              if (Math.abs(self.deltaX) > Math.abs(self.deltaY)) {
                self.event.preventDefault();
                position.current.x = pressProxy - (self.x - self.startX);

                if (isDesktop) {
                  const numCards = cards.length;
                  const singleBlockWidth = numCards * itemWidthWithGap.current;

                  if (position.current.x < singleBlockWidth) {
                    position.current.x += singleBlockWidth;
                    pressProxy += singleBlockWidth;
                  } else if (position.current.x > singleBlockWidth * 4) {
                    position.current.x -= singleBlockWidth;
                    pressProxy -= singleBlockWidth;
                  }
                }

                updatePosition();
              }
            },
            onDragEnd: (self) => {
              carousel.style.cursor = 'grab';
              if (isMobile) {
                const iw = itemWidthWithGap.current;
                let closestIndex = Math.round(position.current.x / iw);

                const flickVelocity = 500;
                if (Math.abs(self.velocityX) > flickVelocity) {
                  closestIndex += self.velocityX < 0 ? 1 : -1;
                } else {
                  const dragAmount = position.current.x - closestIndex * iw;
                  if (dragAmount > iw / 2) {
                    closestIndex++;
                  } else if (dragAmount < -iw / 2) {
                    closestIndex--;
                  }
                }
                animateToCard(closestIndex);
              }
            },
            onWheel: (self) => {
              if (isDesktop) {
                if (Math.abs(self.deltaX) > Math.abs(self.deltaY)) {
                  self.event.preventDefault();
                  activeAnimation?.kill();
                  position.current.x += self.deltaX;

                  const numCards = cards.length;
                  const singleBlockWidth = numCards * itemWidthWithGap.current;

                  if (position.current.x < singleBlockWidth) {
                    position.current.x += singleBlockWidth;
                    pressProxy += singleBlockWidth;
                  } else if (position.current.x > singleBlockWidth * 4) {
                    position.current.x -= singleBlockWidth;
                    pressProxy -= singleBlockWidth;
                  }

                  updatePosition();
                }
              }
            },
          });

          const resizeHandler = () => {
            activeAnimation?.kill();
            calculateSizes();
            const numCards = cards.length;
            const equivalentIndex = (currentIndex % numCards) + numCards * 2;
            animateToCard(equivalentIndex);
          };
          window.addEventListener('resize', resizeHandler);

          return () => {
            observer.kill();
            activeAnimation?.kill();
            clearTimeout(wheelTimeout);
            window.removeEventListener('resize', resizeHandler);
          };
        }
      );
    },
    {
      scope: carouselRef,
      dependencies: [cards.length],
    }
  );

  return (
    <div
      className={`${
        isDarkMode ? '' : 'dark-menu-section'
      } cards-carousel-container`}
    >
      <div
        ref={carouselRef}
        className={`cards-carousel overflow-hidden relative page-wrapper ${className} ${
          !isMobileSize ? 'cards-carousel-padding' : ''
        }`}
        style={{ cursor: 'grab', touchAction: 'pan-y' }}
      >
        <div ref={wrapperRef} className='flex cards-wrapper full-height'>
          {extendedCards.map((card, index) => (
            <div
              key={index}
              className='cards-carousel-item'
              data-card-index={index % cards.length}
              style={{ flexShrink: 0 }}
            >
              <div className='card-content'>
                <ImageCard
                  {...card}
                  imageUrl={
                    isDarkMode
                      ? card.darkModeImageUrl || card.lightModeImageUrl
                      : card.lightModeImageUrl
                  }
                  buttonVariant={isDarkMode ? '' : 'dark'}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CardsCarousel;
